// BRK-S417 — how one row of the submission page's Bonds tab reads: the status
// chip, the bond-number cell before a number exists, and which rows are muted.
//
// Everything here RENDERS a row of the order view's `bonds[]` (PMS-S836, ahead
// of the pin; read by `readBondRows` in bondStateModel.ts). Nothing is derived
// from another lifecycle: a row's chip comes from that row's own `status`,
// and its kind only where the wireframe words one state per kind — a
// maintenance bond not yet requested is "Due at completion".
//
// THE CHIP MAP IS CLOSED. A status this build has not adopted reaches here as
// null (the reader drops the raw value, which could carry vocabulary a broker
// must not read) and reads the neutral "In progress", the same word
// `workflowStatusLabel` uses — never a blank, never the wire value, never a
// throw. `in_underwriting` reads "In review": decision 8, no underwriting
// vocabulary on a broker screen (workflowStatus.ts).

import type { BondRowView, BondStatus } from "@/lib/bondStateModel";
import type { HeaderPill } from "@/lib/submissionShell";

type RowFacts = Pick<BondRowView, "kind" | "status">;

const STATUS_CHIPS: Record<BondStatus, HeaderPill> = {
  not_requested: { label: "Not requested", tone: "neutral" },
  requested: { label: "Pending", tone: "active" },
  in_underwriting: { label: "In review", tone: "attention" },
  // §10.2: approved, issuance being prepared or out for signature.
  approved: { label: "Waiting on Bond", tone: "attention" },
  issued: { label: "Issued / Active", tone: "done" },
  // §10.2: an issued bond an edit cancelled; its reissue is pending.
  voided: { label: "Voided · reissue pending", tone: "attention" },
  converting: { label: "Converting to final", tone: "active" },
  released: { label: "Released", tone: "neutral" },
  exonerated: { label: "Exonerated", tone: "neutral" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};

const DUE_AT_COMPLETION: HeaderPill = {
  label: "Due at completion",
  tone: "neutral",
};

/** What an unadopted status reads as. */
export const UNKNOWN_BOND_STATUS_CHIP: HeaderPill = {
  label: "In progress",
  tone: "neutral",
};

/**
 * A bond row's status chip. `hold` is PMS's own hold on the bid phase (its
 * served note, "On hold · GIA Form needed", BRK-S502): a bid bond on its way
 * reads it, in red, as the current-bond card does.
 */
export function bondRowChip(
  row: RowFacts,
  hold: string | null = null,
): HeaderPill {
  if (row.status === null) return UNKNOWN_BOND_STATUS_CHIP;
  if (hold !== null && row.kind === "bid" && ON_ITS_WAY.includes(row.status)) {
    return { label: hold, tone: "danger" };
  }
  if (row.status === "not_requested" && row.kind === "maintenance")
    return DUE_AT_COMPLETION;
  return STATUS_CHIPS[row.status];
}

/** The statuses of a bond on its way: asked for, with no number yet. */
const ON_ITS_WAY: readonly BondStatus[] = [
  "requested",
  "in_underwriting",
  "approved",
];

/**
 * The Bond no. cell. A number only when PMS published one; otherwise
 * "Pending" for a bond on its way and a dash for one that is not (not yet
 * requested, cancelled, or a status not adopted) — a `placeholder` the table
 * mutes, so it never reads as a bond number.
 */
export function bondNumberCell(
  row: RowFacts & Pick<BondRowView, "bondNumber">,
): {
  text: string;
  placeholder: boolean;
} {
  if (row.bondNumber !== null)
    return { text: row.bondNumber, placeholder: false };
  return {
    text:
      row.status !== null && ON_ITS_WAY.includes(row.status) ? "Pending" : "—",
    placeholder: true,
  };
}

/** The rows the wireframe greys: an obligation that ended (released,
 *  exonerated) and a bond the job has not called for yet. */
const MUTED: readonly BondStatus[] = [
  "not_requested",
  "released",
  "exonerated",
];

export function isMutedBondRow(row: RowFacts): boolean {
  return row.status !== null && MUTED.includes(row.status);
}
